import type { DataTableRowType } from '#components/elements/dataTable/dataTable.model';
import type { DataTableInputType, SortType } from '#domain/performance/kpi/kpi.query';
import type {
  DataTableColumn,
  DataTableDimensionsType,
  DataTableKpiDisplayNameType,
  DataTableViewType
} from '@carrefour-gcs/shared';
import { computed, type Ref } from 'vue';
import { useDataTableRows } from './useDataTableRows';
import { useDataTableTotalRow } from './useDataTableTotalRow';

interface UseDataTableParams {
  dataTableView: Ref<DataTableViewType>;
  selectedDimensions: Ref<DataTableDimensionsType[] | null>;
  selectedKpis: Ref<DataTableKpiDisplayNameType[] | null>;
  sortQueryParams: Ref<SortType | undefined>;
  columnsDefinition: Ref<Record<string, DataTableColumn>>;
  /** Dimensions/sort/columns resolved and canFetchRows is true */
  canFetchRows: Ref<boolean>;
  paginationDefaultValue: number;
  sourceContext?: DataTableInputType['sourceContext'];
  excludeFiltersFromQueryKey?: string[];
  benchmarkQuery?: Ref<DataTableInputType['benchmark']>;
  benchmarkIcon?: Ref<'carrefour' | 'market' | undefined>;
}

export function useDataTable(params: UseDataTableParams) {
  const {
    dataTableView,
    selectedDimensions,
    selectedKpis,
    sortQueryParams,
    columnsDefinition,
    canFetchRows,
    paginationDefaultValue,
    sourceContext,
    excludeFiltersFromQueryKey,
    benchmarkQuery,
    benchmarkIcon
  } = params;

  const { totalRow, isLoadingTotal, isErrorTotal } = useDataTableTotalRow({
    dataTableView,
    selectedKpis,
    columnsDefinition,
    canFetchRows,
    excludeFiltersFromQueryKey
  });

  const {
    rows: detailRows,
    isLoadingData,
    loadMore,
    fetchNextPage
  } = useDataTableRows({
    dataTableView,
    selectedDimensions,
    selectedKpis,
    canFetchRows,
    sortQueryParams,
    columnsDefinition,
    paginationDefaultValue,
    sourceContext,
    excludeFiltersFromQueryKey,
    benchmarkQuery,
    benchmarkIcon
  });

  /**
   * Combined rows: [totalRow?, ...detailRows].
   * TotalRow is cached
   */
  const rows = computed((): DataTableRowType<string>[] => {
    const details = detailRows.value;
    if (!totalRow.value) {
      return details;
    }
    return [totalRow.value, ...details];
  });

  return {
    rows,
    isLoadingData,
    isLoadingTotal,
    isErrorTotal,
    loadMore,
    fetchNextPage
  };
}
